import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, LogOut, AlertTriangle, Smartphone, CreditCard, ArrowLeft, ShieldAlert, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { fullLogout, isOnline } from '@/services/offlineStorage';
import { Capacitor } from '@capacitor/core';

const CONFIRMATION_TEXT = 'DELETE';

const DeleteAccountDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletionInfo, setDeletionInfo] = useState(null);
  const [step, setStep] = useState('info'); // 'info' | 'confirm'
  const [confirmationInput, setConfirmationInput] = useState('');

  const isConfirmationValid = confirmationInput.toUpperCase() === CONFIRMATION_TEXT;

  useEffect(() => {
    if (open) {
      setStep('info');
      setConfirmationInput('');
      fetchDeletionInfo();
    }
  }, [open]);

  const fetchDeletionInfo = async () => {
    setLoading(true);
    try {
      const response = await API.get('/auth/account/deletion-info');
      setDeletionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch deletion info:', error);
      // Fallback info
      setDeletionInfo({
        warning: "Account deletion is PERMANENT and IRREVERSIBLE",
        data_to_be_deleted: {},
        categories: [
          "All medical records",
          "All uploaded documents",
          "All appointments and reminders",
          "All personal data"
        ],
        subscription: { has_active: false }
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear all local data from the device
   */
  const clearAllLocalData = async () => {
    try {
      // 1. Clear Capacitor Preferences (offline medical cache)
      await fullLogout();
      
      // 2. Clear localStorage
      localStorage.clear();
      
      // 3. Clear sessionStorage
      sessionStorage.clear();
      
      // 4. Clear scheduled local notifications (if native)
      if (Capacitor.isNativePlatform()) {
        try {
          const { LocalNotifications } = await import('@capacitor/local-notifications');
          const pending = await LocalNotifications.getPending();
          if (pending.notifications.length > 0) {
            await LocalNotifications.cancel({
              notifications: pending.notifications.map(n => ({ id: n.id }))
            });
          }
        } catch (e) {
          console.log('Could not clear notifications:', e);
        }
        
        // 5. Clear biometric credentials (if available)
        try {
          const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
          await NativeBiometric.deleteCredentials({ server: 'forgetmenot.app' });
        } catch (e) {
          console.log('Could not clear biometric credentials:', e);
        }
      }
      
      // 6. Clear any IndexedDB data
      if (window.indexedDB) {
        const databases = await window.indexedDB.databases?.() || [];
        for (const db of databases) {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        }
      }
      
      // 7. Clear cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
    } catch (error) {
      console.error('Error clearing local data:', error);
      // Continue anyway - backend deletion is what matters
    }
  };

  const handleLogoutInstead = async () => {
    onOpenChange(false);
    await logout();
    navigate('/login');
    toast.success('Logged out successfully', {
      description: 'Your data is safe. Come back anytime.'
    });
  };

  const handleProceedToConfirm = () => {
    setStep('confirm');
    setConfirmationInput('');
  };

  const handleBackToInfo = () => {
    setStep('info');
    setConfirmationInput('');
  };

  const handleDeleteAccount = async () => {
    if (!isConfirmationValid) {
      toast.error('Please type DELETE to confirm');
      return;
    }

    // Check if we're online - account deletion MUST NOT be queued
    const online = await isOnline();
    if (!online) {
      toast.error('Cannot delete account while offline', {
        description: 'Please connect to the internet and try again.',
        duration: 5000
      });
      return;
    }

    setDeleting(true);
    try {
      // Call backend deletion
      const response = await API.delete('/auth/account');
      
      // CRITICAL: Verify this wasn't queued (would be a false success)
      if (response.data?.queued || response._queued) {
        toast.error('Account deletion failed', {
          description: 'Please connect to the internet and try again.',
          duration: 5000
        });
        return;
      }
      
      // Check for partial deletion
      if (response.data?.partial) {
        toast.warning('Account deletion completed with some issues', {
          description: response.data.message || 'Some data may not have been fully removed. Contact support if needed.',
          duration: 8000
        });
      }
      
      // Clear ALL local data
      await clearAllLocalData();
      
      onOpenChange(false);
      
      // Show success with subscription reminder if needed
      if (deletionInfo?.subscription?.has_active) {
        toast.success('Account deleted', {
          description: 'Remember to cancel your subscription in App Store/Play Store settings.',
          duration: 8000
        });
      } else if (!response.data?.partial) {
        toast.success('Account deleted', {
          description: 'All your data has been permanently removed.'
        });
      }
      
      navigate('/login');
      
    } catch (error) {
      console.error('Failed to delete account:', error);
      
      // Handle offline-specific error
      if (error.isOffline) {
        toast.error('Cannot delete account while offline', {
          description: 'Please connect to the internet and try again.',
          duration: 5000
        });
      } else {
        toast.error('Deletion failed', {
          description: error.response?.data?.detail || 'Please try again or contact support.'
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  // Step 1: Information screen
  const renderInfoStep = () => (
    <>
      <div className="px-6 py-5 space-y-5">
        {/* Warning banner */}
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
          <p className="text-sm font-medium text-rose-800">
            This action is PERMANENT and IRREVERSIBLE
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
          </div>
        ) : (
          <>
            {/* What will be deleted */}
            <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-100">
              <p className="text-sm font-medium text-slate-700 mb-3">
                All of the following will be permanently deleted:
              </p>
              <ul className="space-y-2">
                {(deletionInfo?.categories || []).map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-slate-600">
                    <span className="text-rose-400 mt-1">•</span>
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* Data counts */}
              {deletionInfo?.data_to_be_deleted && Object.keys(deletionInfo.data_to_be_deleted).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-2">Your data:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(deletionInfo.data_to_be_deleted).map(([key, value]) => (
                      <span key={key} className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">
                        {value} {key}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Subscription Warning - CRITICAL */}
            {deletionInfo?.subscription?.has_active && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800 mb-2">
                      Active Subscription Detected
                    </p>
                    <p className="text-sm text-amber-700 mb-3">
                      You have an active <strong>{deletionInfo.subscription.tier}</strong> subscription. 
                      Deleting your account here does <strong>NOT</strong> automatically cancel charges.
                    </p>
                    <div className="bg-white/70 rounded-lg p-3 space-y-3">
                      <p className="text-xs font-medium text-amber-800">To stop future charges, cancel your subscription:</p>
                      <a 
                        href="https://apps.apple.com/account/subscriptions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
                      >
                        <Smartphone className="w-4 h-4" />
                        <span><strong>Apple:</strong> Open Subscription Settings →</span>
                      </a>
                      <a 
                        href="https://play.google.com/store/account/subscriptions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-blue-600 hover:underline"
                      >
                        <Smartphone className="w-4 h-4" />
                        <span><strong>Google:</strong> Open Play Store Subscriptions →</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Alternative option */}
            <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-slate-600 mb-3">
                <span className="font-medium">Just need a break?</span> You can log out instead and your data will be here when you return.
              </p>
              <Button
                variant="outline"
                onClick={handleLogoutInstead}
                className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                data-testid="logout-instead-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out instead
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-white border-t border-slate-100">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50"
          data-testid="keep-account-button"
        >
          Keep My Account
        </Button>
        <Button
          variant="destructive"
          onClick={handleProceedToConfirm}
          disabled={loading}
          className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
          data-testid="proceed-to-confirm-button"
        >
          Continue to Delete
        </Button>
      </div>
    </>
  );

  // Step 2: Confirmation screen with text input
  const renderConfirmStep = () => (
    <>
      <div className="px-6 py-5 space-y-5">
        {/* Final warning */}
        <div className="bg-rose-100 border-2 border-rose-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-rose-600 flex-shrink-0" />
            <div>
              <p className="text-base font-bold text-rose-800 mb-1">
                Final Confirmation Required
              </p>
              <p className="text-sm text-rose-700">
                This will permanently delete your account and all data including:
              </p>
              <ul className="text-sm text-rose-700 mt-2 space-y-1">
                <li>• All medical records and documents</li>
                <li>• All uploaded files from our servers</li>
                <li>• All offline data on this device</li>
              </ul>
              <p className="text-sm text-rose-700 mt-2 font-medium">
                This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation input */}
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Type <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-rose-600">{CONFIRMATION_TEXT}</span> to confirm:
            </span>
            <Input
              type="text"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder="Type DELETE here"
              className={`mt-2 text-center text-lg font-mono tracking-widest ${
                confirmationInput && !isConfirmationValid 
                  ? 'border-rose-300 focus:ring-rose-500' 
                  : isConfirmationValid 
                  ? 'border-green-300 focus:ring-green-500 bg-green-50' 
                  : ''
              }`}
              autoComplete="off"
              autoCapitalize="characters"
              data-testid="delete-confirmation-input"
            />
          </label>
          {confirmationInput && !isConfirmationValid && (
            <p className="text-xs text-rose-500 text-center">
              Please type DELETE (case insensitive) to enable the delete button
            </p>
          )}
          {isConfirmationValid && (
            <p className="text-xs text-green-600 text-center font-medium">
              Confirmation accepted. You may now delete your account.
            </p>
          )}
        </div>

        {/* Subscription reminder if applicable */}
        {deletionInfo?.subscription?.has_active && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700">
              <strong>Remember:</strong> Cancel your {deletionInfo.subscription.tier} subscription in App Store/Play Store settings to stop future charges.
            </p>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="px-6 pb-6 pt-2 flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-white border-t border-slate-100">
        <Button
          variant="outline"
          onClick={handleBackToInfo}
          className="flex-1 border-slate-200 text-slate-600 hover:bg-slate-50"
          disabled={deleting}
          data-testid="back-to-info-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
        <Button
          variant="destructive"
          onClick={handleDeleteAccount}
          disabled={deleting || !isConfirmationValid}
          className={`flex-1 text-white transition-all ${
            isConfirmationValid 
              ? 'bg-rose-600 hover:bg-rose-700' 
              : 'bg-rose-300 cursor-not-allowed'
          }`}
          data-testid="delete-account-button"
        >
          {deleting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Deleting Everything...
            </>
          ) : (
            'Permanently Delete Account'
          )}
        </Button>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-gradient-to-b from-blue-50/50 to-white max-h-[90vh] overflow-y-auto">
        {/* Header with warning accent */}
        <div className="bg-gradient-to-r from-rose-100/80 to-orange-100/80 px-6 pt-6 pb-4">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-slate-700">
                  Delete Account
                </DialogTitle>
                {step === 'confirm' && (
                  <p className="text-xs text-rose-600 font-medium mt-0.5">Step 2 of 2 - Final Confirmation</p>
                )}
              </div>
            </div>
          </DialogHeader>
        </div>

        {step === 'info' ? renderInfoStep() : renderConfirmStep()}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
